// b16: Victron, mellan appen, molnet och navet. Navet läser bara: GX-enheten via MQTT på det lokala
// nätet (bara prenumerationer på N/ och keepalive på R/), och enheterna över Bluetooth (Instant
// Readout, med en nyckel per enhet som ägaren skriver in i appen och som förseglas till navet).

import type { Forseglad } from "./lada.ts";

/** Banken som en batteriövervakare mäter, eller ingen. */
export const VICTRONBANKAR = ["forbrukning", "navigation", "ingen"] as const;

export type Victronbank = (typeof VICTRONBANKAR)[number];

export const VICTRONBANKNAMN: Record<Victronbank, string> = {
  forbrukning: "Förbrukningsbanken",
  navigation: "Navigationsbanken",
  ingen: "Ingen bank",
};

/** GX-enhetens adress på platsens nät: ett namn eller en IPv4-adress, med en port om det behövs. */
export const GX_ADRESS =
  /^(?=.{1,253}(:\d{1,5})?$)[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*(:\d{1,5})?$/;

/** VRM Portal ID: tolv tecken hex, som GX-enheten visar under Settings → VRM online portal. */
export const PORTAL_ID = /^[0-9a-f]{12}$/;

/** Nyckeln för en Victron-enhet över Bluetooth, ur VictronConnect: 32 tecken hex. */
export const VICTRON_NYCKEL = /^[0-9a-fA-F]{32}$/;

/** mDNS-namnet som en GX-enhet har som standard, som förslag. */
export const GX_FORSLAG = "venus.local";

/** Det molnet säger till navet om Victron, i varje svar när platsen har något. */
export interface Victronorder {
  /** GX-enheten, eller null. Navet ansluter bara till den adress som ägaren har skrivit in. */
  gx: { adress: string; portal: string | null } | null;
  /** Banken per batteriövervakare, efter fyndets id. Utan rad: ingen bank. */
  bankar: Record<string, Exclude<Victronbank, "ingen">>;
}

/** En nyckel i en förseglad låda, till navet. Molnet kan inte öppna den. */
export interface Nyckellada {
  /** Lådans id i molnet, som navet kvitterar. */
  id: string;
  /** Fyndets id för enheten. Ämnet som lådan är bunden till. */
  fynd: string;
  lada: Forseglad;
}

/** Nyckelns läge för en enhet över Bluetooth, som navet säger det i fynden. */
export type Nyckellage = "saknas" | "fel" | "finns";

/** En Victron-enhet under Inställningar → Victron. */
export interface Victronenhet {
  fynd: string;
  namn: string;
  kalla: "victron" | "bluetooth";
  /** Enheten mäter en bank: SmartShunt, BMV, Lynx Smart BMS, Smart Lithium. */
  batteri: boolean;
  bank: Victronbank;
  /** Nyckeln för en enhet över Bluetooth: saknas, fel, finns, eller skickad (på väg till navet). */
  nyckel: Nyckellage | "skickad" | null;
  /**
   * Den publika nyckeln för lådan hos navet som hör enheten, för att försegla nyckeln. Null: navet
   * kan inte ta emot nycklar.
   */
  lada: string | null;
}

/** Inställningar → Victron. */
export interface Victroninstallningar {
  gx: { adress: string | null; portal: string | null };
  /**
   * b17: GX-enhetens lösenord för MQTT: inget, på väg till navet i en förseglad låda, eller
   * mottaget av navet. Aldrig lösenordet.
   */
  losenord?: "ingen" | "skickad" | "mottagen";
  /** b17: inloggningen enligt navet: utan lösenord, lyckades, fel, eller lösenord krävs. */
  inloggning?: "utan" | "lyckades" | "fel" | "kravs" | null;
  /** Förslaget till adressen: venus.local när navet hittar en GX-enhet där, annars null. */
  forslag: string | null;
  /** Navet har kontakt med GX-enheten, eller null när det inte har sagt något. */
  kontakt: boolean | null;
  enheter: Victronenhet[];
  /**
   * Navets publika nyckel för lådan, för att försegla en nyckel i webbläsaren. Null: navet har
   * ingen (ett nav utan Bluetooth, eller ett äldre nav).
   */
  lada: string | null;
}

/** Var nyckeln finns i VictronConnect, för guiden och Inställningar. */
export const NYCKEL_HJALP =
  "Nyckeln finns i VictronConnect: öppna enheten, tryck på kugghjulet, sedan ⋮ → Product info, och under Instant readout via Bluetooth på Show. Kopiera Encryption data key (32 tecken).";
